import * as fflate from "fflate";

/**
 * Clones an ArrayBuffer or SharedArrayBuffer to create a new ArrayBuffer with the same content.
 *
 * @param buffer The buffer to clone.
 * @returns A new ArrayBuffer with the same content as the input buffer.
 */
export function cloneBuffer(
  buffer: ArrayBuffer | SharedArrayBuffer
): ArrayBuffer {
  const tempArray = new Uint8Array(buffer);
  const arrayBuffer = new ArrayBuffer(tempArray.length);
  new Uint8Array(arrayBuffer).set(tempArray);
  return arrayBuffer;
}

/**
 * Converts ArrayBufferLike to Uint8Array.
 * If the input is a SharedArrayBuffer, it creates a clone to ensure data integrity.
 *
 * @param buffer The ArrayBufferLike to convert.
 * @returns A Uint8Array representing the ArrayBufferLike.
 *
 */

export function bufferToUint8Array(buffer?: null): null;
export function bufferToUint8Array(buffer: ArrayBufferLike): Uint8Array;
export function bufferToUint8Array(
  buffer?: ArrayBuffer | SharedArrayBuffer | null
): Uint8Array | null {
  if (buffer == null) {
    return null;
  }

  // SharedArrayBuffer data is shared and can be modified by multiple threads.
  // If a function needs to process the data in isolation
  // (i.e., ensuring no other thread can modify it during processing),
  // a local copy is needed.
  let arrayBuffer =
    buffer instanceof SharedArrayBuffer ? cloneBuffer(buffer) : buffer;

  const uint8Array = new Uint8Array(arrayBuffer);
  return uint8Array;
}
export function isSharedArrayBufferAvailable() {
  try {
    const sab = new SharedArrayBuffer(1);
    return sab instanceof SharedArrayBuffer;
  } catch (e) {
    return false;
  }
}

/**
 * Creates a SharedArrayBuffer from an ArrayBuffer or Uint8Array.
 * If the input is already a SharedArrayBuffer, the buffer will be cloned.
 *
 * @param data The buffer to share.
 * @returns A new SharedArrayBuffer with the same content as the input buffer.
 */
export const createSharedBuffer = (
  data: Uint8Array<ArrayBufferLike> | ArrayBuffer
) => {
  if (data instanceof ArrayBuffer) {
    data = bufferToUint8Array(data)!;
  }
  const sharedBuffer = new SharedArrayBuffer(data.byteLength);
  new Uint8Array(sharedBuffer).set(data);
  return sharedBuffer;
};

/**
 *
 * Converts a JSON object to a Uint8Array, with optional compression.
 *
 * @param data The JSON object to convert.
 * @param opts Options for conversion, such as whether to compress.
 * @returns A Uint8Array representing the JSON object.
 */
export function convertJsonToUint8Array(
  data: any,
  opts?: { useCompression?: boolean }
): Uint8Array<ArrayBuffer> {
  const jsonString = JSON.stringify(data);
  const array = new TextEncoder().encode(jsonString);
  if (opts?.useCompression) {
    console.debug(
      "Compressing data with fflate.deflateSync...",
      array.byteLength
    );
    return fflate.deflateSync(array, { level: 6 }) as Uint8Array<ArrayBuffer>;
  } else {
    return array;
  }
}

/**
 * Parses a Uint8Array into a JSON object, with optional decompression.
 * @param uint8Array The Uint8Array to parse.
 * @param opts Options for parsing, such as whether to decompress.
 * @returns The parsed JSON object.
 */
export function parseJSONUint8Array<T = any>(
  uint8Array: Uint8Array,
  opts?: { useCompression?: boolean; traceKey?: string }
): T | null {
  if (opts?.useCompression) {
    uint8Array = fflate.inflateSync(uint8Array);
  }

  const jsonString = new TextDecoder().decode(uint8Array);

  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.error(
      `[${opts?.traceKey}] Error parsing JSON from Uint8Array:`,
      error,
      jsonString
    );
    return null;
  }
}
